import React, { useEffect, useState } from 'react';
import { forgotPassword, resetPassword } from '../services/authService';
import './AuthModal.css';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (
    email: string,
    password: string,
    nickname: string
  ) => Promise<void>;
  onKakaoLogin?: () => Promise<void>;
  theme: 'light' | 'dark';
  onShowTermsModal?: () => void;
  onShowPrivacyModal?: () => void;
  initialMode?: 'login' | 'register';
}

type AuthMode = 'login' | 'register' | 'forgotPassword' | 'resetPassword';

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onLogin,
  onRegister,
  onKakaoLogin,
  theme,
  onShowTermsModal,
  onShowPrivacyModal,
  initialMode = 'login',
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [assumeConsent, setAssumeConsent] = useState(false);
  
  // 에러 타입 감지 (잠금, 경고, 일반)
  const getErrorType = (errorMsg: string): 'locked' | 'warning' | 'error' => {
    if (errorMsg.includes('🔒') || errorMsg.includes('잠금')) {
      return 'locked';
    }
    if (errorMsg.includes('⚠️') || errorMsg.includes('마지막 시도')) {
      return 'warning';
    }
    return 'error';
  };

  // URL에서 토큰 확인 (비밀번호 재설정 링크 클릭 시)
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때 initialMode로 설정
      setMode(initialMode);
      
      // 모달이 열릴 때마다 URL에서 토큰 확인
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        console.log('🔑 AuthModal: 비밀번호 재설정 토큰 감지:', token);
        setResetToken(token);
        setMode('resetPassword');
        // URL에서 토큰 제거
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (mode === 'resetPassword' && !resetToken) {
        // 토큰이 없고 resetPassword 모드인데 resetToken도 없으면 initialMode로 전환
        console.log('⚠️ AuthModal: 토큰이 없어서 초기 모드로 전환');
        setMode(initialMode);
        setResetToken(null);
      }
    } else {
      // 모달이 닫힐 때 모드 리셋
      if (mode === 'resetPassword') {
        setMode(initialMode);
        setResetToken(null);
      }
    }
  }, [isOpen, initialMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      if (mode === 'login') {
        await onLogin(email, password);
        onClose();
        resetForm();
      } else if (mode === 'register') {
        await onRegister(email, password, nickname);
        onClose();
        resetForm();
      } else if (mode === 'forgotPassword') {
        await forgotPassword(email);
        setSuccessMessage('비밀번호 재설정 링크가 이메일로 발송되었습니다.');
        setTimeout(() => {
          setMode('login');
          setSuccessMessage('');
          resetForm();
        }, 3000);
      } else if (mode === 'resetPassword') {
        if (newPassword !== confirmPassword) {
          setError('새 비밀번호가 일치하지 않습니다.');
          setIsLoading(false);
          return;
        }
        if (!resetToken) {
          setError('유효하지 않은 토큰입니다.');
          setIsLoading(false);
          return;
        }
        await resetPassword(resetToken, newPassword);
        setSuccessMessage('비밀번호가 성공적으로 변경되었습니다.');
        setTimeout(() => {
          onClose();
          setMode('login');
          resetForm();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setNickname('');
    setResetToken(null);
    setError('');
    setSuccessMessage('');
    setAssumeConsent(false);
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`auth-modal-overlay ${theme === 'dark' ? 'dark-theme' : ''}`}
    >
      <div className={`auth-modal ${theme === 'dark' ? 'dark-theme' : ''}`}>
        <div className="auth-modal-header">
          <div className="auth-header-content">
            <div className="auth-icon">
              {mode === 'login' && '🔐'}
              {mode === 'register' && '✨'}
              {mode === 'forgotPassword' && '🔑'}
              {mode === 'resetPassword' && '🔄'}
            </div>
            <h2>
              {mode === 'login' && '로그인'}
              {mode === 'register' && '회원가입'}
              {mode === 'forgotPassword' && '비밀번호 찾기'}
              {mode === 'resetPassword' && '비밀번호 재설정'}
            </h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'register' && (
            <div className="form-group">
              <label htmlFor="nickname">닉네임</label>
              <input
                type="text"
                id="nickname"
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                placeholder="닉네임을 입력하세요"
                required
                minLength={2}
                maxLength={20}
              />
            </div>
          )}

          {mode !== 'resetPassword' && (
            <div className="form-group">
              <label htmlFor="email">이메일</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="이메일을 입력하세요"
                required
              />
            </div>
          )}

          {mode === 'login' || mode === 'register' ? (
            <div className="form-group">
              <label htmlFor="password">비밀번호</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요"
                required
                minLength={6}
                maxLength={50}
              />
            </div>
          ) : mode === 'resetPassword' ? (
            <>
              <div className="form-group">
                <label htmlFor="newPassword">새 비밀번호</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="새 비밀번호를 입력하세요"
                  required
                  minLength={6}
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">새 비밀번호 확인</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="새 비밀번호를 다시 입력하세요"
                  required
                  minLength={6}
                  maxLength={50}
                />
              </div>
            </>
          ) : null}

          {error && (
            <div 
              className={`error-message ${
                getErrorType(error) === 'locked' 
                  ? 'error-locked' 
                  : getErrorType(error) === 'warning'
                  ? 'error-warning'
                  : ''
              }`}
              style={{
                whiteSpace: 'pre-line', // 줄바꿈 처리
                lineHeight: '1.6',
              }}
            >
              {error}
            </div>
          )}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          {mode === 'register' && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                margin: '12px 0',
                padding: '10px',
                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
                borderRadius: '6px',
              }}>
                <input
                  type="checkbox"
                  id="assumeConsent"
                  checked={assumeConsent}
                  onChange={(e) => {
                    setAssumeConsent(e.target.checked);
                  }}
                  style={{
                    marginRight: '8px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                  }}
                />
                <label
                  htmlFor="assumeConsent"
                  style={{
                    fontSize: '12px',
                    color: theme === 'dark' ? '#999' : '#666',
                    cursor: 'pointer',
                    flex: 1,
                  }}
                >
                  회원가입 시 이용약관 및 개인정보처리방침에 동의한 것으로 간주합니다
                </label>
              </div>
              {!assumeConsent && (
                <div style={{
                  fontSize: '12px',
                  color: theme === 'dark' ? '#999' : '#666',
                  textAlign: 'center',
                  margin: '12px 0',
                  padding: '10px',
                  backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
                  borderRadius: '6px',
                  lineHeight: '1.5',
                }}>
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      if (onShowTermsModal) {
                        onShowTermsModal();
                      }
                    }}
                    style={{ 
                      color: theme === 'dark' ? '#666' : '#999', 
                      textDecoration: 'underline', 
                      marginRight: '8px', 
                      cursor: 'pointer',
                      textDecorationColor: theme === 'dark' ? '#666' : '#999',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                  >
                    이용약관
                  </a>
                  및
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      if (onShowPrivacyModal) {
                        onShowPrivacyModal();
                      }
                    }}
                    style={{ 
                      color: theme === 'dark' ? '#666' : '#999', 
                      textDecoration: 'underline', 
                      marginLeft: '8px', 
                      cursor: 'pointer',
                      textDecorationColor: theme === 'dark' ? '#666' : '#999',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                  >
                    개인정보처리방침
                  </a>
                </div>
              )}
              {assumeConsent && (
                <div style={{
                  fontSize: '12px',
                  color: theme === 'dark' ? '#999' : '#666',
                  textAlign: 'center',
                  margin: '12px 0',
                  padding: '10px',
                  backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
                  borderRadius: '6px',
                  lineHeight: '1.5',
                }}>
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      if (onShowTermsModal) {
                        onShowTermsModal();
                      }
                    }}
                    style={{ 
                      color: theme === 'dark' ? '#666' : '#999', 
                      textDecoration: 'underline', 
                      marginRight: '8px', 
                      cursor: 'pointer',
                      textDecorationColor: theme === 'dark' ? '#666' : '#999',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                  >
                    이용약관
                  </a>
                  및
                  <a 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      if (onShowPrivacyModal) {
                        onShowPrivacyModal();
                      }
                    }}
                    style={{ 
                      color: theme === 'dark' ? '#666' : '#999', 
                      textDecoration: 'underline', 
                      marginLeft: '8px', 
                      cursor: 'pointer',
                      textDecorationColor: theme === 'dark' ? '#666' : '#999',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? '#666' : '#999'}
                  >
                    개인정보처리방침
                  </a>
                </div>
              )}
            </>
          )}

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={isLoading}
          >
            {isLoading
              ? '처리 중...'
              : mode === 'login'
              ? '로그인'
              : mode === 'register'
              ? '회원가입'
              : mode === 'forgotPassword'
              ? '이메일 발송'
              : '비밀번호 재설정'}
          </button>
        </form>

        {mode === 'login' && onKakaoLogin && (
          <>
            <div className="auth-divider">
              <span>또는</span>
            </div>
            <button
              type="button"
              className="kakao-login-btn"
              onClick={onKakaoLogin}
              disabled={isLoading}
            >
              <span className="kakao-icon">kakao</span>
              <span>카카오로 시작하기</span>
            </button>
          </>
        )}

        <div className="auth-switch">
          {mode === 'login' && (
            <>
              <p>
                비밀번호를 잊으셨나요?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('forgotPassword')}
                  className="switch-btn"
                >
                  비밀번호 재설정
                </button>
              </p>
              <p>
                계정이 없으신가요?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('register')}
                  className="switch-btn"
                >
                  회원가입
                </button>
              </p>
            </>
          )}
          {mode === 'register' && (
            <p>
              이미 계정이 있으신가요?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="switch-btn"
              >
                로그인
              </button>
            </p>
          )}
          {mode === 'forgotPassword' && (
            <p>
              비밀번호를 기억하셨나요?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="switch-btn"
              >
                로그인으로 돌아가기
              </button>
            </p>
          )}
          {mode === 'resetPassword' && (
            <p>
              로그인하시겠어요?{' '}
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="switch-btn"
              >
                로그인
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
